/* eslint-disable @typescript-eslint/no-explicit-any */
import { Mail, MapPin, Phone, Send } from "lucide-react";
// import Footer from "./Footer";
// import NavBar from "./NavBar";
import { useAuth } from "../contexts/AuthContext";
import { useState } from "react";
import apiClient from "../api/client";
import { toast } from "sonner";

export default function ContactPage() {
  const { siteData: data } = useAuth();

  const [contactForm, setContactForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    subject: "General Inquiry",
    message: "",
  })
  const [errors, setErrors] = useState<Partial<typeof contactForm>>({});
  const [loading, setLoading] = useState(false)

  const validateContactForm = () => {
    const errors: Partial<typeof contactForm> = {};

    if (!contactForm.firstName.trim()) {
      errors.firstName = "First name is required";
    }

    if (!contactForm.lastName.trim()) {
      errors.lastName = "Last name is required";
    }

    if (!contactForm.email.trim()) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactForm.email)) {
      errors.email = "Enter a valid email address";
    }

    if (!contactForm.subject.trim()) {
      errors.subject = "Subject is required";
    }

    if (!contactForm.message.trim()) {
      errors.message = "Message is required";
    } else if (contactForm.message.length < 5) {
      errors.message = "Message must be at least 5 characters";
    }

    return errors;
  };

  const handleSubmit =async(e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const errors = validateContactForm();
    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      return;
    }

    setLoading(true)
    try{
      const {data} = await apiClient.post(`/admin/send-message`, {
        firstName : contactForm.firstName,
        lastName : contactForm.lastName, 
        email : contactForm.email, 
        message : contactForm.message, 
        subject : contactForm.subject,
      })

      if(data?.success){
        toast.success(data?.message || "Message sent successfully");
        setErrors({});

        setContactForm({
          firstName: "",
          lastName: "",
          email: "",
          subject: "General Inquiry",
          message: "",
        });
      }
      else{
        toast.error(data?.message || "Error while sending message")
      }
    }
    catch(error: any){
      toast.error(error?.response?.data?.message || error?.message || "Error while sending message")
    }
    finally{
      setLoading(false)
    }

  };

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      {/* <NavBar /> */}

      <main className="flex-1 pt-32 pb-20 px-4 bg-gradient-to-b from-[#042033] to-black">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            {/* <h1 className="text-4xl md:text-5xl font-bold mb-4"> */}
            <h1 className="text-white text-3xl font-medium mb-4">
              Get in Touch
            </h1>
            <p className="text-white/60 max-w-2xl mx-auto">
              We'd love to hear from you. Whether you have a question about our
              shows, pricing, need a demo, or anything else, our team is ready
              to answer all your questions.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Info */}
            <div className="lg:col-span-1 space-y-6">
              <div className="bg-white/5 border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-colors">
                <div className="w-12 h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center mb-6">
                  <Phone className="w-6 h-6 text-cyan-400" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Phone</h3>
                <div className="space-y-2">
                  {data &&
                    data?.phones?.map((e: any, i: number) => {
                      return (
                        <div key={i}>
                          <p className="text-white font-medium">{e}</p>
                        </div>
                      );
                    })}
                </div>
              </div>

              <div className="bg-white/5 border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-colors">
                <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center mb-6">
                  <Mail className="w-6 h-6 text-purple-400" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Email</h3>
                <p className="text-white/60 text-sm mb-4">
                  Our friendly team is here to help.
                </p>
                <p className="text-white font-medium">
                  {data ? data?.email : "akaram.radio@gmail.com"}
                </p>
              </div>

              <div className="bg-white/5 border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-colors">
                <div className="w-12 h-12 bg-orange-500/20 rounded-xl flex items-center justify-center mb-6">
                  <MapPin className="w-6 h-6 text-orange-400" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Office</h3>
                <p className="text-white/60 text-sm mb-4">
                  Come say hello at our office HQ.
                </p>
                <p className="text-white font-medium">
                  {data ? data?.address : "123 Radio Station St"}
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2 bg-white/5 border border-white/10 rounded-2xl p-8 md:p-12">
              <form className="space-y-6" onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-white/80">
                      First Name
                    </label>
                    <input
                      type="text"
                      className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/20 focus:outline-none focus:border-cyan-500 transition-all"
                      placeholder="John"
                      value={contactForm.firstName}
                      onChange={(e)=>{setContactForm({...contactForm, firstName: e.target.value})}}
                    />
                    {errors.firstName && <p className="text-red-500 text-sm">{errors.firstName}</p>}
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-white/80">
                      Last Name
                    </label>
                    <input
                      type="text"
                      className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/20 focus:outline-none focus:border-cyan-500 transition-all"
                      placeholder="Doe"
                      value={contactForm.lastName}
                      onChange={(e)=>{setContactForm({...contactForm, lastName: e.target.value})}}
                    />
                    {errors.lastName && <p className="text-red-500 text-sm">{errors.lastName}</p>}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-white/80">
                    Email Address
                  </label>
                  <input
                    type="text"
                    className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/20 focus:outline-none focus:border-cyan-500 transition-all"
                    placeholder="john@example.com"
                    value={contactForm.email}
                    onChange={(e)=>{setContactForm({...contactForm, email: e.target.value})}}
                  />
                  {errors.email && <p className="text-red-500 text-sm">{errors.email}</p>}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-white/80">
                    Subject
                  </label>
                  <select 
                    value={contactForm.subject}
                    onChange={(e)=>{setContactForm({...contactForm, subject: e.target.value})}}
                    // defaultValue={"General Inquiry"}
                    className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-cyan-500 transition-all appearance-none"
                  >
                    <option className="bg-[#042033]" value={"General Inquiry"}>General Inquiry</option>
                    <option className="bg-[#042033]" value={"Sponsorship"}>Sponsorship</option>
                    <option className="bg-[#042033]" value={"Technical Support"}>Technical Support</option>
                    <option className="bg-[#042033]" value={"Feedback"}>Feedback</option>
                  </select>
                  {errors.subject && <p className="text-red-500 text-sm">{errors.subject}</p>}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-white/80">
                    Message
                  </label>
                  <textarea
                    rows={6}
                    value={contactForm.message}
                    onChange={(e)=>{setContactForm({...contactForm, message: e.target.value})}}
                    className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/20 focus:outline-none focus:border-cyan-500 transition-all resize-none"
                    placeholder="How can we help you?"
                  />
                  {errors.message && <p className="text-red-500 text-sm">{errors.message}</p>}
                </div>

                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-semibold py-4 rounded-xl transition-all shadow-lg shadow-cyan-900/20 flex items-center justify-center gap-2"
                >
                  {
                    loading ? 
                    <><SmallLoader /> Sending... </> : 
                    <><Send className="w-5 h-5" /> Send Message </>
                  }
                </button>
              </form>
            </div>
          </div>
        </div>
      </main>

      {/* <Footer /> */}
    </div>
  );
}

export const SmallLoader=()=>{
  return <div className="w-5 h-5 border-2 border-white/80 border-t-transparent rounded-full animate-spin"></div>

}